import React, { useState } from 'react'
import { Modal } from 'react-bootstrap'
import { Button, Avatar } from '@components/common'
import './StartChatModal.css'
import { IFavoritedUser } from '@dataTypes/user-favorite.type'
import { getFullName } from '@utils/fullname.util'

type StartChatModalProps = {
  isOpen: boolean
  onRequestClose: () => void
  onStartChat: (members: string[]) => void
  availableMembers?: IFavoritedUser[]
}

const StartChatModal: React.FC<StartChatModalProps> = ({
  isOpen,
  onRequestClose,
  onStartChat,
  availableMembers = [],
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [selectedMembers, setSelectedMembers] = useState<IFavoritedUser[]>([])

  const MAX_MEMBERS = 50

  const filteredMembers = availableMembers.filter(
    (member) =>
      member.username
        ?.toLowerCase()
        ?.toLowerCase()
        .includes(searchValue.toLowerCase()) ||
      member.email_address
        ?.toLowerCase()
        ?.toLowerCase()
        .includes(searchValue.toLowerCase()),
  )

  const handleMemberSelect = (member: IFavoritedUser): void => {
    if (
      selectedMembers.length < MAX_MEMBERS &&
      !selectedMembers.find((m) => m.user_id === member.user_id)
    ) {
      setSelectedMembers([...selectedMembers, member])
      setSearchValue('')
    }
  }

  const handleRemoveMember = (memberId: string): void => {
    setSelectedMembers(selectedMembers.filter((m) => m.user_id !== memberId))
  }

  const handleClose = (): void => {
    setSelectedMembers([])
    setSearchValue('')
    onRequestClose()
  }

  const handleStartChat = (): void => {
    if (selectedMembers.length > 0) {
      onStartChat(selectedMembers.map((member) => member.user_id!))
      handleClose()
    }
  }

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      className="start-chat-modal"
      backdrop="static"
    >
      <Modal.Header className="bg-custom3" closeButton>
        <Modal.Title>Start chat</Modal.Title>
      </Modal.Header>

      <Modal.Body className="start-chat-modal-body">
        <div className="start-chat-modal-content">
          <div className="start-chat-search-container">
            <input
              type="text"
              className="start-chat-search-input"
              placeholder="Enter the name or email of a team member"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>

          {searchValue && filteredMembers.length > 0 && (
            <div className="start-chat-member-dropdown">
              {filteredMembers
                .filter(
                  (member) =>
                    !selectedMembers.find((m) => m.user_id === member.user_id),
                )
                .map((member) => (
                  <div
                    key={member.user_id}
                    className="start-chat-member-option"
                    onClick={() => handleMemberSelect(member)}
                  >
                    <Avatar
                      source={
                        member.provider_profile?.photo ??
                        'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
                      }
                      alt={`${member.provider_profile ? 'Dr. ' : ''}${getFullName(member)}`}
                      width={32}
                      height={32}
                      style={{ borderRadius: '8px' }}
                    />
                    <div className="start-chat-member-info">
                      <div className="start-chat-member-name">
                        {`${member.provider_profile ? 'Dr. ' : ''}${getFullName(member)}`}
                      </div>
                      <div className="start-chat-member-email">
                        {member.email_address ?? 'N/A'}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}

          <div className="start-chat-member-count">
            Add up to {MAX_MEMBERS} members ({selectedMembers.length}/
            {MAX_MEMBERS})
          </div>

          {selectedMembers.length > 0 && (
            <div className="start-chat-member-badges">
              {selectedMembers.map((member) => (
                <div key={member.user_id} className="start-chat-member-badge">
                  <Avatar
                    source={
                      member.provider_profile?.photo ??
                      'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
                    }
                    alt={`${member.provider_profile ? 'Dr. ' : ''}${getFullName(member)}`}
                    width={16}
                    height={16}
                    style={{ borderRadius: '6px' }}
                  />
                  <span className="start-chat-badge-name">
                    {`${member.provider_profile ? 'Dr. ' : ''}${getFullName(member)}`}
                  </span>
                  <button
                    type="button"
                    className="unstyled-button ti ti-x start-chat-badge-close"
                    aria-label="Remove member"
                    onClick={() => handleRemoveMember(member.user_id!)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="ms-auto">
          <Button
            size="md"
            className="button-secondary me-2"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            size="md"
            className="button-primary"
            onClick={handleStartChat}
            disabled={selectedMembers.length === 0}
          >
            Start chat
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default StartChatModal
